import PropTypes from "prop-types";
import React from "react";

import { Separator, Text } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import styles from "./FlightStopsTracker.module.scss";

/**
 * FlightStopsTracker provides a rendition of a flight showing stops count and change plane caption.
 */
export const FlightStopsTracker = (props) => {
  const { className, destinationOriginAirportCodes, numberStops = 0, stopStations = "" } = props;

  return (
    <div className={classNames(styles.flightStopsTracker, className)}>
      <Separator {...getSeparatorProps()}>{renderNumberStops()}</Separator>
      {renderCaption()}
    </div>
  );

  function renderNumberStops() {
    return numberStops ? (
      <Text className={styles.stopsLabel} color="var(--flightStopsTracker-label)">
        {i18n("FlightStopsTracker__STOPS", { numberStops })}
      </Text>
    ) : null;
  }

  function renderCaption() {
    const displayDestinationOriginAirportCodes = shouldDisplayDestinationOriginAirportCodes();
    let captionText;

    if (numberStops === 0) {
      captionText = i18n("FlightStopsTracker__CAPTION_NONSTOP");
    } else if (stopStations?.length) {
      captionText = i18n("FlightStopsTracker__CAPTION_PLANE_CHANGE", {
        stopStations: getStopStations(),
      });
    } else {
      captionText = i18n("FlightStopsTracker__CAPTION_NO_PLANE_CHANGE");
    }

    return captionText || displayDestinationOriginAirportCodes ? (
      <Text {...getCaptionTextProps()}>
        {displayDestinationOriginAirportCodes && <Text>{destinationOriginAirportCodes[0]}</Text>}
        {captionText && <Text>{captionText}</Text>}
        {displayDestinationOriginAirportCodes && <Text>{destinationOriginAirportCodes[1]}</Text>}
      </Text>
    ) : null;
  }

  function getSeparatorProps() {
    return {
      alignment: "center",
      color: numberStops
        ? "flightStopsTracker-separator-color"
        : "flightStopsTracker-separator-color-nonstop",
      lineCap: "dot",
    };
  }

  function getCaptionTextProps() {
    return {
      className: classNames(styles.caption, {
        [styles.captionWithStationCodes]: shouldDisplayDestinationOriginAirportCodes(),
      }),
      color: "var(--flightStopsTracker-caption)",
      textType: "legal",
    };
  }

  function getStopStations() {
    return Array.isArray(stopStations) ? stopStations.join(", ") : stopStations;
  }

  function shouldDisplayDestinationOriginAirportCodes() {
    return destinationOriginAirportCodes && destinationOriginAirportCodes.length === 2;
  }
};

FlightStopsTracker.propTypes = {
  /** Additional class to be added to the outermost element for positioning the component. */
  className: PropTypes.string,

  /** Airport code(s) of the destination and origin airports.
   * If provided, the component will display the airport codes in the caption.
   */
  destinationOriginAirportCodes: PropTypes.arrayOf(PropTypes.string),

  /** Stops count to be rendered on the flight tracker's graphic. */
  numberStops: PropTypes.number,

  /** Airport code(s) where passengers will need to change planes during the route. */
  stopStations: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
};
